import { useTranslation } from 'react-i18next';

import { ComputedPromotionStatus } from '../../stores/types/promotions.interface';
import { getPromotionStatusLabel } from '../../utils/promotion.util';
import { ColorType, Skeleton } from '../_ui';
import { StatusPill } from './StatusPill.component';

interface IProps {
  status: ComputedPromotionStatus;
  isLoading: boolean;
}

interface PromotionStatusStyle {
  borderColor: ColorType;
  labelColor: ColorType;
  bgColor: ColorType | string;
}

// Colors per status. Rendered through the shared <StatusPill> so the promotion pill is
// pixel-identical to the promo-code pill (PromoCodeStatusPill) wherever it appears.
const STATUS_STYLES: Record<ComputedPromotionStatus, PromotionStatusStyle> = {
  [ComputedPromotionStatus.DRAFT]: {
    borderColor: ColorType.GREY5,
    labelColor: ColorType.GREY5,
    bgColor: ColorType.GREY1,
  },
  [ComputedPromotionStatus.ACTIVE]: {
    borderColor: ColorType.POSITIVE,
    labelColor: ColorType.POSITIVE,
    bgColor: ColorType.POSITIVE_LIGHT,
  },
  [ComputedPromotionStatus.PAUSED]: {
    borderColor: ColorType.PAUSED,
    labelColor: ColorType.PAUSED,
    bgColor: ColorType.WARNING_LIGHT,
  },
  [ComputedPromotionStatus.SCHEDULED]: {
    borderColor: ColorType.DENIM,
    labelColor: ColorType.DENIM,
    bgColor: ColorType.BRAND0,
  },
  [ComputedPromotionStatus.EXPIRED]: {
    borderColor: ColorType.NEGATIVE,
    labelColor: ColorType.NEGATIVE,
    bgColor: ColorType.NEGATIVE_LIGHT,
  },
  [ComputedPromotionStatus.DELETED]: {
    // Border matches the fill (not white) so the archived pill reads as a solid dark pill
    // the same size as the others — matching the promo-code "Archived" pill and Figma.
    borderColor: ColorType.GREY6,
    labelColor: ColorType.WHITE,
    bgColor: ColorType.GREY6,
  },
};

const PromotionStatus = ({ status, isLoading }: IProps) => {
  const { t } = useTranslation();
  const style = STATUS_STYLES[status] ?? STATUS_STYLES[ComputedPromotionStatus.ACTIVE];

  return isLoading ? (
    <Skeleton innerClassName='rounded-3xl' height='h-6' width='w-14' isPill />
  ) : (
    <StatusPill
      label={t(getPromotionStatusLabel(status))}
      borderColor={style.borderColor}
      labelColor={style.labelColor}
      bgColor={style.bgColor}
    />
  );
};

export default PromotionStatus;
